import * as React from 'react'
import { CircularProgress, Grid, Step, StepConnector, withStyles } from '@material-ui/core'
import { BrowserLocaleDateFormatter, BrowserLocaleTimeFormatter, activeTimeFrame, FlowCall, NewMultiSelect, SvgIcon, Title, useInfiniteScroll, Tooltip, useDebounce } from 'prace-common-components'
import { $Arrow, $CallContainer, $Calls, $CallSelect, $CallUID, $Content, $CutoffButton, $CutoffContainer, $Date, $Number, $Status, $StepContainer, $StepIcon, $StepLabel, $Stepper, $SvgIcon } from './styles'
import { useGetCallIdsQuery, useLazyGetCallsDashboardQuery } from 'store/api/calls'
import dateUTC from 'util/dateUTC'
import { CallOverview, DashboardCall, DashboardCutoff, DashboardStepInfo } from './types'
import { useNavigate } from 'react-router-dom'
import { DASHBOARD_STATS, Routes } from 'constants/global'

/* stepSelection value that means "no step selected" — shows the call-level overview */
const OVERVIEW_STEP = -1
/* cutoffSelection value that means "all cutoffs" — step counts are summed across every cutoff.
   Real cutoff ids are positive, so 0 is a safe sentinel. Paired with the overview by default. */
const ALL_CUTOFFS = 0

/* Merge every cutoff's per-step info into one step list whose counts are the sum across all cutoffs.
   App ids are unique per application and each application belongs to a single cutoff, so concatenating
   the id arrays yields the union (lengths add up, no double-counting). */
const sumCutoffSteps = (cutoffs: DashboardCutoff[], stepCount: number): DashboardStepInfo[] =>
  Array.from({ length: stepCount }, (_v, i) => {
    const steps = cutoffs.map((c) => c.steps?.[i]).filter(Boolean) as DashboardStepInfo[]
    const appsAcceptedAssignments: { [k: number]: number } = {}
    steps.forEach((s) => Object.entries(s.appsAcceptedAssignments || {}).forEach(([k, v]) => {
      appsAcceptedAssignments[Number(k)] = (appsAcceptedAssignments[Number(k)] || 0) + (v as number)
    }))
    return {
      appsAcceptedAssignments,
      apps: steps.flatMap((s) => s.apps || []),
      withoutAccepted: steps.flatMap((s) => s.withoutAccepted || []),
      rejectedRevoked: steps.flatMap((s) => s.rejectedRevoked || []),
      withoutAssignments: steps.flatMap((s) => s.withoutAssignments || []),
      submission: steps.flatMap((s) => s.submission || []),
      changesRequest: steps.flatMap((s) => s.changesRequest || []),
      changesRequested: steps.flatMap((s) => s.changesRequested || []),
      approval: steps.flatMap((s) => s.approval || []),
      accepted: steps.flatMap((s) => s.accepted || []),
      acceptance: steps.flatMap((s) => s.acceptance || []),
      expiredSubmission: steps.flatMap((s) => s.expiredSubmission || []),
      submitted: steps.flatMap((s) => s.submitted || []),
      conflicted: steps.flatMap((s) => s.conflicted || []),
      approved: steps.flatMap((s) => s.approved || []),
    }
  })

const OverviewPanel: React.FC<{ overview?: CallOverview }> = ({ overview }) => {
  if (!overview) return null
  const { submitted, draft, adminApproved, awarded, notAwarded, finalReports,
    missingReviews, missingAdminChecks, missingAwards } = overview
  return (
    <>
      <$Content><Title subTitle>Overview</Title></$Content>
      <$Content>
        <$SvgIcon size='22' primary name={DASHBOARD_STATS.SUBMITTED} />
        <$Number>{submitted}</$Number> Submitted applications
      </$Content>
      <$Content>
        <$SvgIcon size='22' primary name={DASHBOARD_STATS.PENDING_SUBMISSION} />
        <$Number>{draft}</$Number> Draft applications
      </$Content>
      {adminApproved !== null ? <$Content>
        <$SvgIcon size='22' primary name={DASHBOARD_STATS.APPROVED} />
        <$Number>{adminApproved}</$Number> Admin approved applications
      </$Content> : null}
      {awarded !== null ? <$Content>
        <$SvgIcon size='22' primary name='trophy' />
        <$Number>{awarded}</$Number> Awarded / <$Number>{notAwarded}</$Number> Not awarded
      </$Content> : null}
      {finalReports !== null ? <$Content>
        <$SvgIcon size='22' primary name='orderApprove' />
        <$Number>{finalReports}</$Number> Submitted final reports
      </$Content> : null}
      {missingReviews ? <$Content>
        <$SvgIcon size='22' name='warning-outlined' />
        <$Number>{missingReviews}</$Number> Missing reviews
      </$Content> : null}
      {missingAdminChecks ? <$Content>
        <$SvgIcon size='22' name='warning-outlined' />
        <$Number>{missingAdminChecks}</$Number> Missing admin checks
      </$Content> : null}
      {missingAwards ? <$Content>
        <$SvgIcon size='22' name='warning-outlined' />
        <$Number>{missingAwards}</$Number> Missing awards
      </$Content> : null}
    </>
  )
}

const ColorlibConnector = withStyles({
  alternativeLabel: {
    top: 13,
  },
  active: {
    backgroundColor: 'black',
    '& $line': {
      backgroundColor: 'black',
    },
  },
  completed: {
    backgroundColor: '#eaeaf0',
    '& $line': {
      backgroundColor: '#eaeaf0',
    },
  },
  line: {
    minWidth: 3,
    marginLeft: -13,
    marginRight: -13,
    height: 3,
    border: 0,
    borderRadius: 1,
    backgroundColor: '#eaeaf0',
  },
})(StepConnector)


const stepIcon = (open: boolean, completed: boolean, pendingActions: boolean, icon: string) => {
  return (
    <$StepIcon className={open ? 'active' : completed ? 'completed' : ''} active={open} completed={completed}>
      <SvgIcon size='16' color='#fff' name={!pendingActions ? 'check' : icon} />
    </$StepIcon>
  )
}

export const Dashboard: React.FC<{isMobile?: boolean}> = () => {
  const navigate = useNavigate()
  const { data: callsData } = useGetCallIdsQuery()
  const [offset, setOffset] = React.useState(0)
  const [loading, setLoading] = React.useState(true)
  const [calls, setCalls] = React.useState<FlowCall[]>([])
  const [selectedCalls, setSelectedCalls] = React.useState<StringNumber[]>(JSON.parse(window.localStorage.getItem('dashboardIds') || '[]') || [])
  const debouncedCalls = useDebounce(selectedCalls, 1000) as StringNumber[]
  const [cutoffSelection, setCutoffSelection] = React.useState<{ [key: number]: number }>({})
  const [stepSelection, setStepSelection] = React.useState<{ [key: number]: number }>({})
  const [getDashboardCall] = useLazyGetCallsDashboardQuery()

  const fetchDashboardCall = React.useCallback(async (offset: number, callIds?: number[]) => {
    try {
      setLoading(true)
      const { data: fetchedCall } = await getDashboardCall({ limit: 1, offset, callIds })
      if (fetchedCall?.data) {
        const call = fetchedCall.data[0]
        /* Default to the call-level overview (no step selected) paired with all cutoffs */
        setStepSelection((stepSelection) =>
          offset ? ({ ...stepSelection, [call.id]: OVERVIEW_STEP }) : { [call.id]: OVERVIEW_STEP })
        setCutoffSelection((selection) =>
          offset ? ({ ...selection, [call.id]: ALL_CUTOFFS }) : { [call.id]: ALL_CUTOFFS })
        setCalls((prev) => offset ? [...prev, call] : [call])
        setOffset(offset)
        setLoading(false)
      }
    } catch (err) {
      console.log(err)
      setLoading(false)
    }
  }, [getDashboardCall])
  
  React.useEffect(() => {
    fetchDashboardCall(0, debouncedCalls as number[])
  }, [fetchDashboardCall, debouncedCalls])

  const onCutoffSelect = (callId: number, cutoffId: number) => {
    setCutoffSelection((prev) => ({ ...prev, [callId]: cutoffId }))
  }

  const onSelectCalls = (calls: StringNumber[]) => {
    setSelectedCalls(calls)
    window.localStorage.setItem('dashboardIds', JSON.stringify(calls))
  }

  const navigateWithState = (state: any) => {
    if(!state.ids?.length) return
    window.localStorage.setItem('dashboardState', JSON.stringify(state))
    navigate(Routes.APPLICATIONS)
  }

  const callItems = callsData?.data?.filter((c) => c.published)
    .map((call) => ({ value: call.id, label: call.uid })) || []

  const hasMore = (offset + 1) < (debouncedCalls?.length ? debouncedCalls.length : callItems.length)
  const { loadMoreRef } = useInfiniteScroll(
    () => fetchDashboardCall(offset + 1, debouncedCalls as number[]), hasMore && !loading)

  return (
    <>
      <$Calls>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <Title bold alternate fontSize={20}>Ongoing Calls</Title>
          </Grid>
          <Grid item xs={6} md={4}>
            <$CallSelect>
              <NewMultiSelect
                title='Selected Calls'
                hideItalic
                name='calls'
                items={callItems}
                onChange={(_n, value) => onSelectCalls(value)}
                value={selectedCalls as string[]}
              />
            </$CallSelect>
          </Grid>
        </Grid>
        <div>
          {calls.map((call) => {
            const selectedCutoffId = cutoffSelection[call.id] ?? ALL_CUTOFFS
            const isAllCutoffs = selectedCutoffId === ALL_CUTOFFS
            /* All cutoffs: synthesize a cutoff whose step counts are summed across every cutoff */
            const selectedCutoff = isAllCutoffs
              ? { steps: sumCutoffSteps(call.cutoffs as DashboardCutoff[], call.flow.length) } as DashboardCutoff
              : call.cutoffs.find((cutoff) => cutoff.id == selectedCutoffId) as DashboardCutoff
            const selectedStepIdx = stepSelection[call.id] ?? OVERVIEW_STEP
            const isOverview = selectedStepIdx < 0
            /* Overview scoped to the selected cutoff, or the whole-call overview for all cutoffs */
            const overviewData = isAllCutoffs ? (call as DashboardCall).overview : selectedCutoff?.overview
            const selectedStep = call.flow[selectedStepIdx]
            const hasApproval = selectedStep?.config?.hasAdminCheck
            /* A step has one timeframe per cutoff. Under All cutoffs there is no single cutoffId to match
               (ALL_CUTOFFS is the sentinel 0), so consider every timeframe — the step counts as active or
               opened if any of them is, matching the summed step counts shown alongside. Used by both the
               detail panel and the stepper below, which must agree on whether a step is open. */
            const timeframesForSelection = (step?: FlowCall['flow'][number]) => (step?.timeframes || [])
              .filter((timeframe) => isAllCutoffs || timeframe.cutoffId == selectedCutoffId)
            const cutoffLabel = (cutoffId?: number) => {
              const cutoff = call.cutoffs.find((c) => c.id == cutoffId)
              return cutoff ? `CutOff ${cutoff.title || ((cutoff.order || 0) + 1)}` : ''
            }
            const stepTimeframes = timeframesForSelection(selectedStep)
            const activeStepTimeframe = activeTimeFrame({ ...selectedStep, timeframes: stepTimeframes })
            const stepActive = !!activeStepTimeframe
            /* Display the active timeframe, else the latest one (timeframes are ordered by cutoff). */
            const selectedStepTimeframe = activeStepTimeframe || stepTimeframes[stepTimeframes.length - 1]
            const selectedStepInfo = selectedCutoff?.steps[selectedStepIdx]
            /* This will be shown in steps after the first one */
            const stepOpened = stepTimeframes
              .some((timeframe) => new Date(timeframe.start).getTime() <= new Date().getTime())
            const firstStepHasApproval = call.flow[0].config?.hasAdminCheck
            const firstStepApproved = selectedCutoff?.steps[0]?.approved || []
            const assignmentsWithOnlyRejected = selectedStepInfo?.rejectedRevoked?.filter((id) =>
              !selectedStepInfo.submitted.includes(id) && !selectedStepInfo.submission.includes(id) 
              && !selectedStepInfo.expiredSubmission.length) || []
            const approvedWithoutAssignments = selectedStepInfo?.withoutAssignments?.filter((id) =>
              firstStepApproved.includes(id)) || []
            const totalApprovedWithoutAssignments = [...assignmentsWithOnlyRejected, ...approvedWithoutAssignments]

            const totalApprovals = selectedStepInfo?.submitted.length || 0
            const totalDrafts = (selectedStepInfo?.submission.length || 0) + 
              (selectedStepInfo?.expiredSubmission.length || 0) + 
              (selectedStepInfo?.submitted.length || 0)

            const hasMinAssignments = selectedStep?.config?.minAssignments || 0
            const totalAccepts = hasMinAssignments ? 0 :
              (selectedStepInfo?.acceptance.length || 0) + (selectedStepInfo?.accepted.length || 0)

            const approvedWithoutAccepted = selectedStepInfo?.withoutAccepted?.filter((id) =>
              firstStepApproved.includes(id)) || []

            const missingAcceptanceApps: {[k: string]: string[]} = {}
            Object.keys(selectedStepInfo?.appsAcceptedAssignments || {}).forEach((key) => {
              const number = selectedStepInfo.appsAcceptedAssignments[Number(key)]
              if(number >= hasMinAssignments) return
              if(!missingAcceptanceApps[number]) missingAcceptanceApps[number] = [key]
              else missingAcceptanceApps[number].push(key)
            })

            const totalChangesRequested = (selectedStepInfo?.changesRequest.length || 0) + 
              (selectedStepInfo?.changesRequested.length || 0)
            return (
              <$CallContainer key={call.id}>
                <div>
                  <Grid container alignItems='baseline'>
                    <div>{call.title}</div>
                    <$CallUID>{call.uid}</$CallUID>
                  </Grid>
                </div>
                <$CutoffContainer wrap='nowrap' container alignItems='center'>
                  <$CutoffButton
                    clickable={!isAllCutoffs}
                    selected={isAllCutoffs}
                    onClick={() => onCutoffSelect(call.id, ALL_CUTOFFS)}
                  >
                    All cutoffs
                  </$CutoffButton>
                  {call.cutoffs.map((cutoff) =>
                    <$CutoffButton
                      clickable={!!call.cutoffs.length && (selectedCutoffId != cutoff.id)}
                      selected={selectedCutoffId == cutoff.id}
                      key={cutoff.id}
                      onClick={() => onCutoffSelect(call.id, cutoff.id || 0)}
                    >
                      {cutoffLabel(cutoff.id)}
                    </$CutoffButton>)}
                </$CutoffContainer>
                <$Stepper alternativeLabel activeStep={0} connector={<ColorlibConnector />}>
                  <Step key='overview'>
                    <$StepLabel
                      onClick={() => setStepSelection((prev) => ({ ...prev, [call.id]: OVERVIEW_STEP }))}
                      StepIconComponent={
                        () => <Tooltip placement='top' show noIcon text='Overview'>
                          <$StepIcon overview className={isOverview ? 'active' : ''} active={isOverview}>
                            <SvgIcon size='16' color='#fff' name='dashboard' />
                          </$StepIcon>
                        </Tooltip>
                      }
                    >
                      {isOverview ? <$Arrow /> : ' '}
                    </$StepLabel>
                  </Step>
                  {call.flow.map((step, index) => {
                    const cutoffStepInfo = selectedCutoff?.steps[index]
                    /* Same selection filter as the detail panel above, so the icon can't read Closed
                       while the panel next to it reads Active under All cutoffs. */
                    const cutoffTimeframes = timeframesForSelection(step)
                    const stepTimeframe = activeTimeFrame({ ...step, timeframes: cutoffTimeframes })
                    const cutoffTimeframe = stepTimeframe || cutoffTimeframes[cutoffTimeframes.length - 1]

                    const endTimeframe = cutoffTimeframe?.end ? dateUTC(cutoffTimeframe.end) : undefined
                    
                    const now = dateUTC()
                    const active = !!stepTimeframe
                    const completed = active || !endTimeframe ? false : (endTimeframe < now)
                    const icon = cutoffStepInfo ?
                      cutoffStepInfo.changesRequest.length ? DASHBOARD_STATS.REQUEST_CHANGES :
                        cutoffStepInfo.submission.length ? DASHBOARD_STATS.PENDING_SUBMISSION : 
                          cutoffStepInfo.approval.length ? DASHBOARD_STATS.PENDING_APPROVAL : 
                            cutoffStepInfo.acceptance.length ? DASHBOARD_STATS.PENDING_ACCEPTANCE : active ? 'pending' : 'lockOutlined' : active ? 'pending' : 'lockOutlined'

                    const pendingActions = 
                      (cutoffStepInfo?.submission.length || cutoffStepInfo?.changesRequest.length)
                        || cutoffStepInfo?.approval.length || 
                        (completed ? 0 : cutoffStepInfo?.acceptance.length)
                    return (
                      <Step key={step.id}>
                        <$StepLabel
                          onClick={() => setStepSelection((prev) => ({ ...prev, [call.id]: index }))}
                          StepIconComponent={
                            () => <Tooltip
                              placement='top'
                              show
                              noIcon
                              text={step.title}
                            >
                              {stepIcon(active, completed || isAllCutoffs, !!pendingActions, icon)}
                            </Tooltip>
                          }
                        >
                          {selectedStepIdx == index ? <$Arrow /> : ' '}
                        </$StepLabel>
                      </Step>
                    )})}
                </$Stepper>
                <$StepContainer>
                  {isOverview ? <OverviewPanel overview={overviewData} /> : <>
                    <$Content><Title subTitle>{selectedStep?.title}</Title></$Content>
                    {selectedStepTimeframe ? 
                      <div>
                        {/* Under All cutoffs the dates belong to one cutoff, so name it. */}
                        {isAllCutoffs ? `${cutoffLabel(selectedStepTimeframe.cutoffId)} timeframe from ` : 'Timeframe from '}
                        <$Date>{`${BrowserLocaleDateFormatter.format(new Date(selectedStepTimeframe.start))} ${BrowserLocaleTimeFormatter.format(new Date(selectedStepTimeframe.start))}`}</$Date>
                        <$Date>{selectedStepTimeframe?.end ? ` to ${BrowserLocaleDateFormatter.format(new Date(selectedStepTimeframe.end))} ${BrowserLocaleTimeFormatter.format(new Date(selectedStepTimeframe.end))}` : ' with no end'}</$Date>
                      </div>
                      : null}
                    <$Content>Status: <$Status>{stepActive ? 'Active' : 'Closed'}</$Status></$Content>
                    {firstStepHasApproval && stepOpened && selectedStepIdx > 0
                      && totalApprovedWithoutAssignments.length ?
                      <$Content onClick={() => 
                        navigateWithState({ name: DASHBOARD_STATS.WITHOUT_ASSIGNMENTS,
                          step: selectedStep, call, ids: totalApprovedWithoutAssignments })}>
                        <$SvgIcon size='22' primary={!totalApprovedWithoutAssignments.length} name={DASHBOARD_STATS.SUBMITTED} />
                        <$Number>{totalApprovedWithoutAssignments.length}/{firstStepApproved.length}</$Number> 
                    Proposals without assignments
                      </$Content> : null}
                    {selectedStepInfo?.submission.length ? <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.PENDING_SUBMISSION,
                        step: selectedStep, call, ids: selectedStepInfo.submission })}>
                      <$SvgIcon size='22' primary={!selectedStepInfo.submission.length} name={DASHBOARD_STATS.PENDING_SUBMISSION} />
                      <$Number>{selectedStepInfo.submission.length}/{totalDrafts}</$Number> 
                      {!selectedStepIdx ? 'Drafts' : 'Pending Submissions'}
                    </$Content> : null}
                    {selectedStepInfo?.expiredSubmission.length ? <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.MISSED_SUBMISSION,
                        step: selectedStep, call, ids: selectedStepInfo.expiredSubmission })}>
                      <$SvgIcon size='22' primary name={DASHBOARD_STATS.MISSED_SUBMISSION} />
                      <$Number>{selectedStepInfo.expiredSubmission.length}/{totalDrafts}</$Number>
                    Missed Submission Deadline
                    </$Content> : null}
                    <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.SUBMITTED,
                        step: selectedStep, call, ids: selectedStepInfo?.submitted })}>
                      <$SvgIcon size='22' primary name={DASHBOARD_STATS.SUBMITTED} />
                      <$Number>{selectedStepInfo?.submitted.length || 0}</$Number> Submitted
                    </$Content>
                    {selectedStepInfo?.changesRequest.length ? <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.REQUEST_CHANGES,
                        step: selectedStep, call, ids: selectedStepInfo.changesRequest })}>
                      <$SvgIcon size='22' primary={!selectedStepInfo.changesRequest.length} name={DASHBOARD_STATS.REQUEST_CHANGES} />
                      <$Number>{selectedStepInfo.changesRequest.length}/{totalChangesRequested}</$Number>
                    Pending Request Changes
                    </$Content> : null}
                    {!hasMinAssignments && selectedStepInfo?.acceptance.length ? <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.PENDING_ACCEPTANCE,
                        step: selectedStep, call, ids: selectedStepInfo.acceptance })}>
                      <$SvgIcon size='22' primary={!selectedStepInfo.acceptance.length} name={DASHBOARD_STATS.PENDING_ACCEPTANCE} />
                      <$Number>
                        {selectedStepInfo.acceptance.length}/{totalAccepts}
                      </$Number>
                    Pending Acceptance
                    </$Content> : null}
                    {hasMinAssignments && stepOpened ? 
                      Object.keys(missingAcceptanceApps).map((key) => <$Content key={key} onClick={() => 
                        navigateWithState({ name: DASHBOARD_STATS.PENDING_ACCEPTANCE,
                          step: selectedStep, call, ids: missingAcceptanceApps[key] })}>
                        <$SvgIcon size='22' primary={false} name={DASHBOARD_STATS.PENDING_ACCEPTANCE} />
                        <$Number>
                          {`${missingAcceptanceApps[key].length} proposals missing ${key} acceptance${Number(key) > 1 ? 's' : ''}`}
                        </$Number>
                      </$Content>)
                      : null}
                    {/* TODO: Implement this for all steps (even without approval) when we have step types: */}
                    {hasMinAssignments && firstStepHasApproval && stepOpened && approvedWithoutAccepted?.length ?
                      <$Content onClick={() => 
                        navigateWithState({ name: DASHBOARD_STATS.PENDING_ACCEPTANCE,
                          step: selectedStep, call, ids: approvedWithoutAccepted })}>
                        <$SvgIcon size='22' primary={false} name={DASHBOARD_STATS.PENDING_ACCEPTANCE} />
                        <$Number>
                          {`${approvedWithoutAccepted.length} 
                        proposals missing ${hasMinAssignments} acceptance${hasMinAssignments > 1 ? 's' : ''}`}
                        </$Number>
                      </$Content>
                      : null}
                    {hasApproval && (selectedStepInfo?.approval.length) ? <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.PENDING_APPROVAL,
                        step: selectedStep, call, ids: selectedStepInfo.approval })}>
                      <$SvgIcon size='22' primary={!selectedStepInfo.approval.length} name={DASHBOARD_STATS.PENDING_APPROVAL} />
                      <$Number>
                        {selectedStepInfo.approval.length}/{totalApprovals}
                      </$Number>
                    Pending Approval
                    </$Content> : null}
                    {hasApproval ? <$Content onClick={() => 
                      navigateWithState({ name: DASHBOARD_STATS.APPROVED,
                        step: selectedStep, call, ids: selectedStepInfo?.approved })}>
                      <$SvgIcon size='22' primary name={DASHBOARD_STATS.APPROVED} />
                      <$Number>{selectedStepInfo?.approved.length || 0}</$Number> Approved
                    </$Content> : null}
                    {/* <$Content onClick={() => //TODO: add conflict of interest when it is revamped
                    navigateWithState({ name: DASHBOARD_STATS.CONFLICT_INTEREST,
                      step: selectedStep, call, ids: selectedStepInfo?.conflicted })}>
                    <$SvgIcon size='22' primary={!selectedStepInfo?.conflicted.length} 
                    name={DASHBOARD_STATS.CONFLICT_INTEREST} />
                    <$Number>{selectedStepInfo?.conflicted.length || 0}</$Number> Conflicted
                  </$Content> */}
                  </>}
                </$StepContainer>
              </$CallContainer>
            )})}
        </div>
        {loading && <Grid container justifyContent='center'>
          <CircularProgress size={30} />
        </Grid>}
        {!!hasMore && !loading && <div ref={loadMoreRef}>...</div>}
      </$Calls>
    </>
  )
}
